import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { converterApi } from '@services/api';
import { HistoryTable } from '@components/history/HistoryTable';
import {
  HistoryFilters,
  FilterValues,
} from '@components/history/HistoryFilters';
import { Pagination } from '@components/history/Pagination';
import { ConversionDetailsModal } from '@components/history/ConversionDetailsModal';
import type {
  Conversion,
  ConversionHistory,
  ConversionStatus,
  SourceSystem,
} from '@types';

export default function History() {
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [history, setHistory] = useState<ConversionHistory | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedConversion, setSelectedConversion] =
    useState<Conversion | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Parse filters from URL
  const getFiltersFromUrl = (): FilterValues => {
    return {
      status:
        (searchParams.get('status') as ConversionStatus | null) || undefined,
      source_system:
        (searchParams.get('source_system') as SourceSystem | null) || undefined,
      start_date: searchParams.get('start_date') || undefined,
      end_date: searchParams.get('end_date') || undefined,
      search: searchParams.get('search') || undefined,
    };
  };

  const [filters, setFilters] = useState<FilterValues>(getFiltersFromUrl());
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get('page') || '1', 10)
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    parseInt(searchParams.get('per_page') || '25', 10)
  );

  // Update URL when filters/pagination change
  const updateUrl = useCallback(
    (newFilters: FilterValues, page: number, perPage: number) => {
      const params = new URLSearchParams();

      if (newFilters.status) params.set('status', newFilters.status);
      if (newFilters.source_system)
        params.set('source_system', newFilters.source_system);
      if (newFilters.start_date)
        params.set('start_date', newFilters.start_date);
      if (newFilters.end_date) params.set('end_date', newFilters.end_date);
      if (newFilters.search) params.set('search', newFilters.search);
      if (page > 1) params.set('page', page.toString());
      if (perPage !== 25) params.set('per_page', perPage.toString());

      setSearchParams(params);
    },
    [setSearchParams]
  );

  // Fetch history data
  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await converterApi.getConversionHistory({
        page: currentPage,
        per_page: itemsPerPage,
        ...filters,
      });
      setHistory(data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to load conversion history');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, filters]);

  // Initial load
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchHistory();
    }, 10000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchHistory]);

  // Debounced search
  useEffect(() => {
    if (!filters.search) return;

    const timeout = setTimeout(() => {
      setCurrentPage(1);
      updateUrl(filters, 1, itemsPerPage);
    }, 500);

    return () => clearTimeout(timeout);
  }, [filters, filters.search, itemsPerPage, updateUrl]);

  const handleFiltersChange = (newFilters: FilterValues) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    updateUrl(filters, 1, itemsPerPage);
    fetchHistory();
  };

  const handleClearFilters = () => {
    const emptyFilters: FilterValues = {};
    setFilters(emptyFilters);
    setCurrentPage(1);
    updateUrl(emptyFilters, 1, itemsPerPage);
    fetchHistory();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateUrl(filters, page, itemsPerPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (perPage: number) => {
    setItemsPerPage(perPage);
    setCurrentPage(1);
    updateUrl(filters, 1, perPage);
  };

  const handleViewDetails = (conversion: Conversion) => {
    setSelectedConversion(conversion);
    setIsModalOpen(true);
  };

  const handleDownload = async (conversion: Conversion) => {
    try {
      const blob = await converterApi.downloadConvertedFile(conversion.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `converted_${conversion.file_name}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download file');
    }
  };

  const handleRetry = async (conversion: Conversion) => {
    if (!confirm(`Retry conversion for "${conversion.file_name}"?`)) {
      return;
    }

    try {
      await converterApi.retryConversion(conversion.id);
      toast.success('Conversion retry initiated');
      fetchHistory();
    } catch (error) {
      console.error('Retry failed:', error);
      toast.error('Failed to retry conversion');
    }
  };

  const handleDelete = async (conversion: Conversion) => {
    if (
      !confirm(
        `Are you sure you want to delete this conversion?\n\nFile: ${conversion.file_name}\nID: ${conversion.id}\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      // Optimistic update
      setHistory(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          conversions: prev.conversions.filter(c => c.id !== conversion.id),
          total: prev.total - 1,
        };
      });

      await converterApi.deleteConversion(conversion.id);
      toast.success('Conversion deleted successfully');

      // Refresh to get accurate count
      fetchHistory();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete conversion');
      // Revert optimistic update on error
      fetchHistory();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Conversion History
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                View and manage your payroll file conversions
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Auto-refresh toggle */}
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={e => setAutoRefresh(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Auto-refresh (10s)
              </label>
              {/* Manual refresh button */}
              <button
                onClick={fetchHistory}
                disabled={isLoading}
                className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowPathIcon
                  className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
                />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <HistoryFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
        />

        {/* Results count */}
        {history && !isLoading && (
          <div className="mb-4">
            <p className="text-sm text-gray-700">
              Found <span className="font-medium">{history.total}</span>{' '}
              conversion
              {history.total !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* Table */}
        <HistoryTable
          conversions={history?.conversions || []}
          isLoading={isLoading}
          onViewDetails={handleViewDetails}
          onDownload={handleDownload}
          onRetry={handleRetry}
          onDelete={handleDelete}
        />

        {/* Pagination */}
        {history && history.total > 0 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={history.total_pages}
              totalItems={history.total}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        )}

        {/* Details Modal */}
        <ConversionDetailsModal
          isOpen={isModalOpen}
          conversion={selectedConversion}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedConversion(null);
          }}
          onDownload={handleDownload}
          onRetry={handleRetry}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
}
